import { Stack } from "@mui/material";
import type { FC } from "react";
import {
	ClientCarBodyTypeEnum,
	ClientCarFuelEnum,
	ClientPropertyCarVehicleTypeEnum,
	type ClientPropertyVehicleDetailResponseDTO,
	ClientPropertyVehicleTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailRow } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRow";
import { AutoMotoBasicInfoDetails } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/Property/AutoMoto/AutoMotoBasicInfoDetails";
import { PropertyExtendedParams } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/Property/PropertyExtendedParams";
import { CAR_SECURITY_TYPES } from "web_myplann/Portfolio/Helpers/carSecurityTypes";
import { CAR_SPECIAL_EQUIPMENT } from "web_myplann/Portfolio/Helpers/carSpecialEquipment";
import { formatEnumLabel } from "web_myplann/Portfolio/Helpers/formatEnumLabel";
import { useGetPropertyOptionalParams } from "web_myplann/Portfolio/Hooks/useGetPropertyOptionalParams";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import { unitFormatter } from "web_myplann/Shared/Utils/numberFormatters";
import { UNITS } from "web_myplann/Shared/Utils/units";

const PageResources = Resources.PortfolioDetail;

type Props = {
	data: ClientPropertyVehicleDetailResponseDTO;
	currencySign: string;
};

export const AutoMotoRedesign: FC<Props> = ({ data }) => {
	const { t } = useResource();

	const { properties: carSecurity } = useGetPropertyOptionalParams(
		CAR_SECURITY_TYPES,
		data,
		t,
	);
	const { properties: carSpecialEquipment } = useGetPropertyOptionalParams(
		CAR_SPECIAL_EQUIPMENT,
		data,
		t,
	);

	const carType = formatEnumLabel(
		data.propertyType,
		ClientPropertyVehicleTypeEnum.None,
		PageResources.Property.CarPropertyTypeValue,
		t,
	);

	const carBodyType = formatEnumLabel(
		data.carBodyType,
		ClientCarBodyTypeEnum.None,
		PageResources.Property.CarBodyTypeValue,
		t,
	);

	const carFuel = formatEnumLabel(
		data.carFuel,
		ClientCarFuelEnum.None,
		PageResources.Property.CarFuelTypeValue,
		t,
	);

	const isOtherElectro =
		data.propertyType === ClientPropertyVehicleTypeEnum.OtherElectro;

	const carVehicleType = isOtherElectro
		? formatEnumLabel(
				data.carVehicleType,
				ClientPropertyCarVehicleTypeEnum.None,
				PageResources.Property.CarVehicleTypeValue,
				t,
			)
		: null;
	const carSerialNumber = isOtherElectro ? data.carSerialNumber : null;
	const carMaxSpeed = isOtherElectro
		? unitFormatter(data.carMaxSpeed, UNITS.kilometerPerHour)
		: null;

	const carPower = unitFormatter(data.carPower, UNITS.kilowatt);
	const carVolume = unitFormatter(data.carVolume, UNITS.cubicCentimeter);
	const carWeight = unitFormatter(data.carWeight, UNITS.kilogram);
	const carSeatCount = data.carSeatCount?.toLocaleString("cs-CZ");
	const carRegistration = dateFormatter(data.carRegistration);
	const carPutIntoService = dateFormatter(data.carPutIntoService);

	return (
		<>
			<Stack bgcolor="background.paper">
				<DetailRow
					label={t(PageResources.Property.CarVehicleType)}
					value={carVehicleType}
				/>
				<DetailRow
					label={t(PageResources.Property.CarBrand)}
					value={data.carBrand}
				/>
				<DetailRow
					label={t(PageResources.Property.CarModel)}
					value={data.carModel}
				/>
				<DetailRow
					label={t(PageResources.Property.CreatedAt)}
					value={data.carManufactureMonthYear}
				/>
				<DetailRow
					label={t(PageResources.Property.CarLicensePlate)}
					value={data.carLicensePlate}
				/>
				<DetailRow
					label={t(PageResources.Property.CarVin)}
					value={data.carVin}
				/>
				<DetailRow
					label={t(PageResources.Property.CarPurchaseDate)}
					value={dateFormatter(data.carPurchaseDate)}
				/>
				<PropertyExtendedParams
					noun="vehicle"
					values={[
						carType,
						carFuel,
						carPower,
						carVolume,
						carBodyType,
						carWeight,
						carSeatCount,
						carRegistration,
						carPutIntoService,
						carSerialNumber,
						carMaxSpeed,
						carSecurity,
						carSpecialEquipment,
					]}
				>
					<DetailRow
						label={t(PageResources.Property.CarPropertyType)}
						value={carType}
					/>
					<DetailRow
						label={t(PageResources.Property.CarFuelType)}
						value={carFuel}
					/>
					<DetailRow
						label={t(PageResources.Property.CarPower)}
						value={carPower}
					/>
					<DetailRow
						label={t(PageResources.Property.CarVolume)}
						value={carVolume}
					/>
					<DetailRow
						label={t(PageResources.Property.CarBodyType)}
						value={carBodyType}
					/>
					<DetailRow
						label={t(PageResources.Property.CarWeight)}
						value={carWeight}
					/>
					<DetailRow
						label={t(PageResources.Property.CarSeatCount)}
						value={carSeatCount}
					/>
					<DetailRow
						label={t(PageResources.Property.CarRegistration)}
						value={carRegistration}
					/>
					<DetailRow
						label={t(PageResources.Property.CarPutIntoService)}
						value={carPutIntoService}
					/>
					<DetailRow
						label={t(PageResources.Property.CarSerialNumber)}
						value={carSerialNumber}
					/>
					<DetailRow
						label={t(PageResources.Property.CarMaxSpeed)}
						value={carMaxSpeed}
					/>
					<DetailRow
						label={t(PageResources.Property.CarSecurity)}
						value={carSecurity}
					/>
					<DetailRow
						label={t(PageResources.Property.CarSpecialEquipment)}
						value={carSpecialEquipment}
					/>
				</PropertyExtendedParams>
			</Stack>

			<AutoMotoBasicInfoDetails data={data} />
		</>
	);
};
